// lib
import { useState, useRef, useEffect } from 'react'
import PropTypes from 'prop-types'
import classNames from 'classnames'

import { getImageAssetPath, IMAGE_ASSET_PATH_FILTER_RESIZE_TO_LIMIT } from '@aytm/lib/common/assets'
import { configure as configureImageAssetUploadManager } from '@aytm/lib/rfd-ui/components/assets/jobs/image-asset-upload-manager'

// components
// import { Image } from 'primereact/image'
import { Button } from 'primereact/button'

import ImagePreview from '@aytm/app/survey/editor/components/content/assets/ImagePreview'

import UploadAssetsButton from '@aytm/lib/rfd-ui/components/assets/upload-assets-button'

import QuestionHeader from '@aytm/app/stats/page/question-views/components/QuestionHeader'
import QuestionBody from '@aytm/app/stats/page/question-views/components/QuestionBody'
import QuestionAnnotation from '@aytm/app/stats/page/question-views/components/QuestionAnnotation'

// styles
import style from '@aytm/app/stats/page/question-views/sn/styles/Component.module.scss'

const Component = ({ model, features, isQuestionCollapsed, onChange, onImageLoad, onAIAddQuestionClick, ...rest }) => {
  const [title, setTitle] = useState(model.text)
  const [asset, setAsset] = useState(model.asset)
  const [note, setNote] = useState(model.note || '')

  const [collapsed, setCollapsed] = useState(isQuestionCollapsed)

  const [loaded, setLoaded] = useState(false)

  const [editing, setEditing] = useState(false)
  const [uploading, setUploading] = useState(false)

  const [errorText, setErrorText] = useState(null)

  const timer = useRef(null)

  const imageRef = useRef()
  const imagePlaceholderHeightRef = useRef(0)

  const savedValues = useRef(null)
  savedValues.current = { title: model.text, note: model.note, asset: model.asset }

  const hasImage = asset && Object.hasOwn(asset, 'path')
  const { disabled } = features

  const isSaveDisabled =
    disabled ||
    uploading ||
    title == undefined ||
    !title.trim().length ||
    (title === savedValues.current.title && (note || '') === (model.note || '') && asset === savedValues.current.asset)

  configureImageAssetUploadManager({
    createAssetPath: 'report/image_asset/create'
  })

  const onUploadAsset = res => {
    setUploading(false)

    if (res && res[0]) {
      const asset = res[0]
      const showError = asset && ['invalid', 'error', 'failed'].includes(asset.status)
      if (!showError) {
        const image = {
          uid: asset.assetUid,
          url: asset.imageUrl,
          path: asset.imagePath,
          original: asset.original
        }

        if (imageRef.current) {
          const el = imageRef.current.previewImage
          const rect = el.getBoundingClientRect()
          imagePlaceholderHeightRef.current = rect.height
        }
        setErrorText(null)
        setAsset(image)
      } else {
        const error = asset.error?.length
          ? asset.error.join(' ')
          : "We couldn't upload your file, please check your connection, file and try again. 30MB max."
        setErrorText(error)
      }
    }
  }

  const onDeleteAsset = () => {
    setAsset(null)
  }

  const onChangeText = html => {
    setNote(html || '')
  }

  const onStartEditing = () => {
    setEditing(true)
  }

  const onCancel = () => {
    setTitle(savedValues.current.title)
    setAsset(savedValues.current.asset)
    setNote(savedValues.current.note || '')
    setUploading(false)
    setErrorText(null)
    setEditing(false)
  }

  const onSetEditing = isEdit => {
    isEdit ? onStartEditing() : onCancel()
  }

  const onSave = (withThisNote = note) => {
    if (title !== savedValues.current.title || withThisNote !== model.note || asset !== savedValues.current.asset) {
      const updatedNote = withThisNote.replace(/^(?:\s*<br\b[^>]*>\s*)+|(?:\s*<br\b[^>]*>\s*)+$/gi, '').trim()
      setNote(updatedNote)
      onChange({
        text: title,
        note: updatedNote,
        asset
      })
    }
    setUploading(false)
    setEditing(false)
    setErrorText(null)
  }

  const onImageLoaded = () => {
    loaded && onImageLoad(imageRef.current.previewImage, imagePlaceholderHeightRef.current)
    setLoaded(true)
  }

  useEffect(() => {
    loaded && onImageLoad(imageRef.current.previewImage, imagePlaceholderHeightRef.current)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loaded])

  useEffect(() => {
    setTitle(model.text)
    setAsset(model.asset)
    setNote(model.note || '')
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [model.text, model.asset.uid, model.note])

  useEffect(() => {
    timer.current && clearTimeout(timer.current)
    timer.current = setTimeout(
      () => {
        setCollapsed(isQuestionCollapsed)
      },
      !isQuestionCollapsed ? 0 : 1000
    )

    return () => timer.current && clearTimeout(timer.current)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isQuestionCollapsed])

  const renderImage = () =>
    hasImage ? (
      <div className={`${style.row} ${style['row-image']}`}>
        {editing && !uploading ? (
          <div onClick={onDeleteAsset} className={style.deleteAsset}>
            <i className="icon-aytmfd-trash" />
          </div>
        ) : null}
        <ImagePreview
          ref={imageRef}
          className={loaded ? '' : 'p-hidden'}
          maxHeight="600px"
          maxWidth="600px"
          imageUrl={asset.original?.url || asset.url}
          previewUrl={getImageAssetPath({
            path: asset.original?.path || asset.path,
            width: 1000,
            height: 1000,
            filter: IMAGE_ASSET_PATH_FILTER_RESIZE_TO_LIMIT,
            unsharp: true,
            backgroundColor: 'transparent'
          })}
          onImageLoaded={onImageLoaded}
        />
      </div>
    ) : null

  return (
    <>
      <QuestionHeader
        model={model}
        features={features}
        titleExt={title}
        isEditingExt={editing}
        isQuestionCollapsed={isQuestionCollapsed}
        onSetTitleExt={setTitle}
        onSetEditQuestionExt={onSetEditing}
        onSaveExt={onSave}
        {...rest}
      />
      {!collapsed && (
        <QuestionBody editing={editing} isQuestionCollapsed={isQuestionCollapsed}>
          {0 < note?.length && !editing ? (
            <>
              {renderImage()}
              <QuestionAnnotation
                className={style.annotation}
                id={model.id}
                note={note || ''}
                editing={false}
                isNoteEditable={false}
                disabled={true}
              />
            </>
          ) : (
            <div className={style.container}>
              {editing ? (
                <>
                  <div className={style.row}>
                    <div className={`${style.help} p-col`}>
                      Type in/paste your executive summary below. It will be added as a slide on PPTX exports.
                    </div>
                    <div className={`${style.changeAsset} p-right p-col-4`}>
                      <UploadAssetsButton
                        type="image"
                        text={hasImage ? 'Replace illustration' : 'Add an illustration'}
                        withOriginal={true}
                        withErrors={true}
                        renderProgressText={() => 'Uploading your image'}
                        onFilesUpload={() => setUploading(true)}
                        onFilesUploaded={onUploadAsset}
                      />
                    </div>
                  </div>
                  {errorText && (
                    <div className={`${style.row} ${style.error}`} dangerouslySetInnerHTML={{ __html: errorText }} />
                  )}
                  {renderImage()}
                  {disabled ? (
                    <>
                      {0 < note?.length ? (
                        <QuestionAnnotation
                          className={style.annotation}
                          id={model.id}
                          note={note || ''}
                          editing={false}
                          isNoteEditable={false}
                          disabled={true}
                        />
                      ) : null}
                      <div className={`${style.row} p-col-12`}>
                        <div className="p-field p-col">&nbsp;</div>
                        <div className={`${style.group} p-fluid p-right p-col-4`}>
                          <Button
                            id={`stat-annotation-cancel-button-${model.id}`}
                            className={classNames(
                              'stat-annotation-cancel-button -dtr-stat-annotation-cancel-button',
                              style.button,
                              style.cancel,
                              'p-button-secondary'
                            )}
                            label="CANCEL"
                            onClick={onCancel}
                          />
                          <Button
                            id={`stat-annotation-save-button-${model.id}`}
                            className={classNames(
                              'stat-annotation-save-button -dtr-stat-annotation-save-button',
                              style.button
                            )}
                            label="SAVE"
                            disabled={isSaveDisabled}
                            onClick={isSaveDisabled ? null : onSave}
                          />
                        </div>
                      </div>
                    </>
                  ) : (
                    <QuestionAnnotation
                      className={style['annotation-editing']}
                      id={model.id}
                      note={note || ''}
                      editing={editing}
                      isNoteEditable={!disabled}
                      isSaveDisabled={isSaveDisabled}
                      disabled={disabled}
                      setNote={onChangeText}
                      onSaveEditing={onSave}
                      onCancelEditing={onCancel}
                      onAIAddQuestionClick={onAIAddQuestionClick}
                    />
                  )}
                </>
              ) : (
                <>
                  {renderImage()}
                  {hasImage || disabled ? null : (
                    <div className={`${style.row} ${style.help}`}>Add an executive summary/notes to your report.</div>
                  )}
                </>
              )}
            </div>
          )}
        </QuestionBody>
      )}
    </>
  )
}

Component.propTypes = {
  model: PropTypes.shape({
    id: PropTypes.string,
    key: PropTypes.string,
    text: PropTypes.string,
    asset: PropTypes.object,
    note: PropTypes.string
  }),
  features: PropTypes.object,
  isQuestionCollapsed: PropTypes.bool,
  onChange: PropTypes.func.isRequired,
  onImageLoad: PropTypes.func,
  onAIAddQuestionClick: PropTypes.func
}

Component.displayName = 'Component'

export default Component
